import { reviewWorkspaceChannel, type ReviewWorkspaceMessage } from './review-workspace';

type ReviewChannel = Pick<BroadcastChannel, 'onmessage' | 'postMessage'>;
type ReviewOwner = (event: MessageEvent<ReviewWorkspaceMessage>) => void;
/** One primary queue survives Jobs/Settings/Learning navigation. Barriers and
 * preceding detached commands must reach this same queue, not racing listeners. */
export class PrimaryReviewChannel {
  private owner: ReviewOwner | undefined;
  readonly channel: ReviewChannel;
  constructor(channel: ReviewChannel, barrier: (requestId: string, participantId: number) => void) {
    this.channel = channel;
    channel.onmessage = (event: MessageEvent<ReviewWorkspaceMessage>) => {
      const message = event.data;
      if (message.type === 'close-permission-barrier') {
        barrier(message.requestId, message.participantId);
        return;
      }
      if (this.owner) {
        this.owner(event);
        return;
      }
      if (message.type === 'request-state' || message.type === 'action') {
        channel.postMessage({ type: 'state', snapshot: null } satisfies ReviewWorkspaceMessage);
        if (
          message.type === 'action' &&
          message.action.type === 'accept-line-item' &&
          message.action.requestId
        )
          channel.postMessage({
            type: 'row-confirmation-result',
            requestId: message.action.requestId,
            documentMutationToken: null,
          } satisfies ReviewWorkspaceMessage);
      } else if (message.type === 'field-draft-command' || message.type === 'line-draft-command') {
        // No view owns native editing here. Answer explicit submissions instead
        // of leaving the originating draft falsely stuck in 'saving'.
        const line = message.type === 'line-draft-command';
        if (message.command.type === 'submit')
          channel.postMessage({
            type: line ? 'line-draft-result' : 'field-draft-result',
            draft: message.command.draft,
            outcome: {
              status: 'refused',
              reason: "Open this document's job before saving. Your typing is kept.",
            },
          } satisfies ReviewWorkspaceMessage);
        else if (message.command.type === 'discard')
          channel.postMessage({
            type: line ? 'line-draft-discarded' : 'field-draft-discarded',
            draft: message.command.draft,
          } satisfies ReviewWorkspaceMessage);
      }
    };
  }
  attach(owner: ReviewOwner) {
    this.owner = owner;
    return () => {
      if (this.owner !== owner) return;
      this.owner = undefined;
      this.channel.postMessage({ type: 'state', snapshot: null } satisfies ReviewWorkspaceMessage);
    };
  }
}

let primary: PrimaryReviewChannel | undefined;
export const primaryReviewChannel = (barrier: (requestId: string, participantId: number) => void) =>
  (primary ??= new PrimaryReviewChannel(new BroadcastChannel(reviewWorkspaceChannel), barrier));
